import { useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import { postIpc } from "../../lib/ipc";
import { useLayoutSurfaceReport } from "../../lib/layoutTooling";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";

type CanvasBlock = {
  id: string;
  title: string;
  kind: string;
  column: number;
  row: number;
  width: number;
  height: number;
  tone: string;
  body: string;
  chart_path?: string;
  footer?: string;
  svg_markup?: string;
};

type CanvasItem = {
  id: string;
  title: string;
  prompt: string;
  saved_kind: string;
  updated_at: string;
  request_label: string;
  summary: string;
  render_mode?: string;
  seed: number;
  blocks: CanvasBlock[];
};

type CanvasEntry = {
  id: string;
  title: string;
  saved_kind: string;
  updated_at: string;
};

type CanvasPayload = {
  project_name: string;
  prompt_hint: string;
  current?: CanvasItem | null;
  library: CanvasEntry[];
};

type CanvasMode = "icon" | "block" | "scene" | "3d-scene";

type ComposerItem = {
  id: string;
  title: string;
  kind: string;
  tone: string;
  svg_markup?: string;
};

const MODE_COPY: Record<CanvasMode, { title: string; body: string; kicker: string }> = {
  icon: {
    kicker: "icon forge",
    title: "single mark studio",
    body: "Generate one compact symbol and keep the slab focused on inspection and remix.",
  },
  block: {
    kicker: "widget lab",
    title: "single block studio",
    body: "Generate one focused widget and refine it before promoting it into a bigger canvas.",
  },
  scene: {
    kicker: "scene builder",
    title: "multi-block canvas",
    body: "Generate coordinated blocks, rearrange them, and stage a reusable layout.",
  },
  "3d-scene": {
    kicker: "immersive plate",
    title: "background scene studio",
    body: "Generate one full background plate for overlays, ambient scenes, and future 3d treatments.",
  },
};

function CanvasBrandLock({ mode }: { mode: CanvasMode }) {
  return (
    <div className="canvas-brand-lock" aria-hidden="true">
      <span className="canvas-brand-lock__wordmark">
        <span className="canvas-brand-lock__chevron">&gt;</span>
        <span className="canvas-brand-lock__text">opentool</span>
      </span>
      <span className="canvas-brand-lock__copy">drafting {mode === "3d-scene" ? "3d scene" : mode}</span>
    </div>
  );
}

function DynamicChart({ tone, path }: { tone: string; path: string }) {
  return (
    <svg viewBox="0 0 120 44" className={`canvas-mini-chart tone-${tone}`} aria-hidden="true">
      <path d={path} />
      <path d={path} className="glow" />
    </svg>
  );
}

function DynamicPie({ tone, segments }: { tone: string; segments: string }) {
  const [a = 33, b = 33, c = 34] = segments.split(",").map((value) => Number(value) || 0);
  const total = Math.max(1, a + b + c);
  const stops = [a / total, (a + b) / total];
  return (
    <svg viewBox="0 0 120 120" className={`canvas-pie tone-${tone}`} aria-hidden="true">
      <circle cx="60" cy="60" r="30" pathLength={1} style={{ strokeDasharray: `${stops[0]} 1` }} />
      <circle
        cx="60"
        cy="60"
        r="30"
        pathLength={1}
        className="segment-b"
        style={{ strokeDasharray: `${stops[1] - stops[0]} 1`, strokeDashoffset: `-${stops[0]}` }}
      />
      <circle
        cx="60"
        cy="60"
        r="30"
        pathLength={1}
        className="segment-c"
        style={{ strokeDasharray: `${1 - stops[1]} 1`, strokeDashoffset: `-${stops[1]}` }}
      />
    </svg>
  );
}

function ChartFace({ tone, path, caption }: { tone: string; path: string; caption: string }) {
  return (
    <div className="canvas-chart-face">
      <DynamicChart tone={tone} path={path} />
      <p>{caption}</p>
    </div>
  );
}

function PieFace({ tone, segments, caption }: { tone: string; segments: string; caption: string }) {
  return (
    <div className="canvas-pie-face">
      <DynamicPie tone={tone} segments={segments} />
      <p>{caption}</p>
    </div>
  );
}

function MetricFace({ tone, value }: { tone: string; value: string }) {
  return (
    <div className={`canvas-metric tone-${tone}`}>
      <strong>{value}</strong>
    </div>
  );
}

function ListFace({ rows }: { rows: string[] }) {
  return (
    <ul className="canvas-list">
      {rows.map((row, index) => (
        <li key={`${row}-${index}`}>{row}</li>
      ))}
    </ul>
  );
}

function MapFace({ rows }: { rows: string[] }) {
  return (
    <div className="canvas-map">
      <div className="canvas-map__grid" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <ul className="canvas-map__list">
        {rows.map((row, index) => (
          <li key={`${row}-${index}`}>{row}</li>
        ))}
      </ul>
    </div>
  );
}

function renderBlockBody(block: CanvasBlock) {
  if (block.svg_markup && block.svg_markup.trim()) {
    return <div className="canvas-svg-face" dangerouslySetInnerHTML={{ __html: block.svg_markup }} />;
  }

  if (block.kind === "chart") {
    return (
      <ChartFace
        tone={block.tone}
        path={block.chart_path || "M4 35 L25 29 L45 31 L65 17 L86 19 L116 8"}
        caption={block.body}
      />
    );
  }

  if (block.kind === "pie") {
    return <PieFace tone={block.tone} segments={block.chart_path || "33,33,34"} caption={block.body} />;
  }

  if (block.kind === "metric") {
    return <MetricFace tone={block.tone} value={block.body} />;
  }

  if (block.kind === "list") {
    return <ListFace rows={block.body.split("\n").filter(Boolean)} />;
  }

  if (block.kind === "map") {
    return <MapFace rows={block.body.split("\n").filter(Boolean)} />;
  }

  return <p>{block.body}</p>;
}

export function CanvasWorkbench({ payload }: { payload?: unknown }) {
  const view = (payload as CanvasPayload | undefined) ?? {
    project_name: "workspace",
    prompt_hint: "Describe a visualization to generate.",
    library: [],
  };
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<CanvasMode>("block");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [positions, setPositions] = useState<Record<string, { column: number; row: number }>>({});
  const [composer, setComposer] = useState<ComposerItem[]>([]);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const current = view.current ?? null;
  const isDrafting = Boolean(current && current.blocks.length === 0);
  const activeMode = mode;

  useEffect(() => {
    const next = Object.fromEntries(
      (current?.blocks ?? []).map((block) => [block.id, { column: block.column, row: block.row }]),
    );
    setPositions(next);
  }, [current?.id, current?.seed]);

  useEffect(() => {
    if (!current?.render_mode) return;
    setMode(current.render_mode as CanvasMode);
  }, [current?.id, current?.render_mode]);

  const summary = useMemo(() => {
    if (!current) return "No draft yet.";
    return `${current.blocks.length} blocks · ${current.saved_kind}${current.seed ? ` · ${current.seed % 10000}` : ""}`;
  }, [current]);

  const studioCopy = useMemo(() => MODE_COPY[activeMode], [activeMode]);

  useLayoutSurfaceReport({
    route: "canvas",
    surfaceId: "canvas-studio",
    rootRef,
    selection: {
      projectId: view.project_name,
      canvasId: current?.id ?? "draft",
    },
    routes: [
      {
        id: "canvas-studio",
        title: "Canvas Studio",
        kind: "studio",
        canvas_id: current?.id ?? "draft",
        built_in_route: "canvas",
        valid: true,
        fallback_route_id: "canvas-studio",
      },
    ],
    deps: [current?.id ?? "", current?.updated_at ?? "", current?.blocks.length ?? 0, activeMode, composer.length],
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = prompt.trim();
    if (!value) return;
    postIpc({ kind: "canvas-generate", data: JSON.stringify({ prompt: value, mode }) });
    setPrompt("");
  };

  const handleGridDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!draggingId || !gridRef.current || activeMode !== "scene") return;
    const rect = gridRef.current.getBoundingClientRect();
    const cellWidth = rect.width / 8;
    const cellHeight = 76;
    const column = Math.min(8, Math.max(1, Math.floor((event.clientX - rect.left) / cellWidth) + 1));
    const row = Math.max(1, Math.floor((event.clientY - rect.top) / cellHeight) + 1);
    setPositions((previous) => ({ ...previous, [draggingId]: { column, row } }));
    setDraggingId(null);
  };

  const addBlockToComposer = (block: CanvasBlock) => {
    setComposer((previous) => [
      ...previous,
      {
        id: `${block.id}-${previous.length + 1}`,
        title: block.title,
        kind: block.kind,
        tone: block.tone,
        svg_markup: block.svg_markup,
      },
    ]);
  };

  const removeComposerItem = (id: string) => {
    setComposer((previous) => previous.filter((item) => item.id !== id));
  };

  return (
    <main className="ui-shell canvas-shell" ref={rootRef}>
      <section className={`canvas-workbench canvas-workbench--${activeMode}`} data-layout-node="canvas" data-layout-id="canvas.root" data-layout-kind="canvas" data-layout-title="canvas studio">
        <div className="canvas-menubar" aria-hidden="true">
          <span>File</span>
          <span>Edit</span>
          <span>Generate</span>
          <span>Layout</span>
          <span>Library</span>
          <span>View</span>
          <span>Help</span>
        </div>

        <aside className="canvas-studio-sidebar" data-layout-node="slab" data-layout-id="canvas.sidebar" data-layout-kind="slab" data-layout-title="studio rail" data-grid-column="1" data-grid-row="1" data-grid-width="2" data-grid-height="12" data-cap-internal-scroll="true">
          <div className="canvas-studio-sidebar__panel" data-layout-node="block" data-layout-id="canvas.sidebar.mode" data-layout-kind="block" data-layout-title="mode details" data-layout-parent="canvas.sidebar">
            <span>{studioCopy.kicker}</span>
            <strong>{studioCopy.title}</strong>
            <em>{studioCopy.body}</em>
          </div>
          <div className="canvas-studio-sidebar__panel" data-layout-node="block" data-layout-id="canvas.sidebar.composer" data-layout-kind="block" data-layout-title="composer summary" data-layout-parent="canvas.sidebar">
            <span>Composer</span>
            <strong>{composer.length} staged</strong>
            <em>Use the `+` action on a generated block to collect it into a custom canvas draft.</em>
          </div>
        </aside>

        <section className="canvas-main" data-layout-node="slab" data-layout-id="canvas.main" data-layout-kind="slab" data-layout-title="main studio" data-grid-column="3" data-grid-row="1" data-grid-width="8" data-grid-height="12">
          <header className="canvas-topbar" data-layout-node="block" data-layout-id="canvas.topbar" data-layout-kind="block" data-layout-title="studio controls" data-layout-parent="canvas.main">
            <div className="canvas-topbar__title">
              <span>Canvas</span>
              <strong>{view.project_name}</strong>
            </div>
            <form className="canvas-prompt" onSubmit={submit}>
              <div className="canvas-tabbar" role="tablist" aria-label="canvas studio mode">
                <button type="button" role="tab" aria-selected={mode === "icon"} className={mode === "icon" ? "is-active" : ""} onClick={() => setMode("icon")}>
                  icon
                </button>
                <button type="button" role="tab" aria-selected={mode === "block"} className={mode === "block" ? "is-active" : ""} onClick={() => setMode("block")}>
                  block
                </button>
                <button type="button" role="tab" aria-selected={mode === "scene"} className={mode === "scene" ? "is-active" : ""} onClick={() => setMode("scene")}>
                  scene
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "3d-scene"}
                  className={mode === "3d-scene" ? "is-active" : ""}
                  onClick={() => setMode("3d-scene")}
                >
                  3d scene
                </button>
              </div>
              <input
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={view.prompt_hint}
                autoComplete="off"
              />
              <button type="submit">generate</button>
            </form>
          </header>

          <CodexTerminalFrame
            title={current?.request_label ?? "canvas draft"}
            prompt={current?.title ?? "Describe a visualization."}
            className="canvas-request"
            compact
          >
            <div className="canvas-request__meta">
              <span>{activeMode === "3d-scene" ? "background plate" : activeMode}</span>
              <em>{current?.summary ?? "Generate a block, scene, or immersive background plate."}</em>
            </div>
            <div className="canvas-request__body">
              <p>
                {current?.prompt ??
                  "Prompt the canvas here. Use icon for marks, block for a single widget, scene for coordinated panels, or 3d scene for an immersive background plate."}
              </p>
            </div>
          </CodexTerminalFrame>

          <section className={`canvas-grid-shell mode-${activeMode}`}>
            {isDrafting ? (
              <div className="canvas-lock-screen">
                <CanvasBrandLock mode={activeMode} />
              </div>
            ) : null}
            <div
              ref={gridRef}
              className={`canvas-grid mode-${activeMode}${draggingId ? " is-dragging" : ""}`}
              data-layout-node="block"
              data-layout-id="canvas.grid"
              data-layout-kind="block"
              data-layout-title="canvas grid"
              data-layout-parent="canvas.main"
              data-cap-move="true"
              data-cap-resize="true"
              data-cap-stage="true"
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleGridDrop}
            >
                {current ? (
                  current.blocks.map((block) => (
                  <article
                    key={block.id}
                    className={`canvas-block tone-${block.tone}`}
                    data-layout-node="block"
                    data-layout-id={`canvas.block.${block.id}`}
                    data-layout-kind="block"
                    data-layout-title={block.title}
                    data-layout-parent="canvas.grid"
                    data-grid-column={positions[block.id]?.column ?? block.column}
                    data-grid-row={positions[block.id]?.row ?? block.row}
                    data-grid-width={block.width}
                    data-grid-height={block.height}
                    data-cap-move={activeMode === "scene" ? "true" : "false"}
                    data-cap-stage="true"
                    data-cap-remix="true"
                    draggable={!isDrafting && activeMode === "scene"}
                    onDragStart={() => setDraggingId(block.id)}
                    onDragEnd={() => setDraggingId(null)}
                    style={{
                      gridColumn:
                        activeMode === "scene"
                          ? `${positions[block.id]?.column ?? block.column} / span ${block.width}`
                          : "1 / -1",
                      gridRow:
                        activeMode === "scene"
                          ? `${positions[block.id]?.row ?? block.row} / span ${block.height}`
                          : "1 / span 1",
                    }}
                  >
                    <div className="canvas-block__header">
                      <span>{block.kind}</span>
                      <div className="canvas-block__title-row">
                        <strong>{block.title}</strong>
                        <button
                          type="button"
                          className="canvas-block__stage"
                          onClick={() => addBlockToComposer(block)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {renderBlockBody(block)}
                    {block.footer ? <footer>{block.footer}</footer> : null}
                  </article>
                ))
              ) : (
                <div className="canvas-empty">
                  <strong>Describe a visualization.</strong>
                  <p>Examples: time series of request latency, incident board, coverage map, 3d scene, release health view.</p>
                </div>
              )}
            </div>
          </section>
        </section>

        <aside className="canvas-library" data-layout-node="slab" data-layout-id="canvas.library" data-layout-kind="slab" data-layout-title="canvas library" data-grid-column="11" data-grid-row="1" data-grid-width="2" data-grid-height="12" data-cap-internal-scroll="true">
          <div className="canvas-library__header">
            <strong>Library</strong>
            <span>{summary}</span>
          </div>
          <div className="canvas-library__actions">
            <button type="button" onClick={() => postIpc({ kind: "canvas-save-widget", data: "" })} disabled={!current}>
              save widget
            </button>
            <button type="button" onClick={() => postIpc({ kind: "canvas-save-canvas", data: "" })} disabled={!current}>
              save canvas
            </button>
          </div>
          <div className="canvas-library__list" data-layout-node="block" data-layout-id="canvas.library.items" data-layout-kind="block" data-layout-title="saved items" data-layout-parent="canvas.library" data-cap-internal-scroll="true">
            {view.library.map((item) => (
              <button
                key={item.id}
                type="button"
                className="canvas-library__item"
                onClick={() => postIpc({ kind: "canvas-select", data: item.id })}
              >
                <strong>{item.title}</strong>
                <span>{item.saved_kind}</span>
              </button>
            ))}
          </div>

          <div className="canvas-composer" data-layout-node="block" data-layout-id="canvas.library.composer" data-layout-kind="block" data-layout-title="composer" data-layout-parent="canvas.library" data-cap-internal-scroll="true">
            <div className="canvas-composer__header">
              <strong>Custom canvas</strong>
              <button type="button" onClick={() => setComposer([])} disabled={composer.length === 0}>
                clear
              </button>
            </div>
            <div className="canvas-composer__grid">
              {composer.length === 0 ? (
                <div className="canvas-composer__empty">
                  <strong>Stage widgets here</strong>
                  <p>Use the `+` action on generated blocks to assemble a layout.</p>
                </div>
              ) : (
                composer.map((item) => (
                  <article key={item.id} className={`canvas-composer__card tone-${item.tone}`}>
                    <header>
                      <span>{item.kind}</span>
                      <button type="button" onClick={() => removeComposerItem(item.id)}>
                        remove
                      </button>
                    </header>
                    <strong>{item.title}</strong>
                    {item.svg_markup ? (
                      <div
                        className="canvas-composer__visual"
                        dangerouslySetInnerHTML={{ __html: item.svg_markup }}
                      />
                    ) : null}
                  </article>
                ))
              )}
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
